import React, { useState, useEffect } from 'react';
import {
    Star, Users, X, User, MessageCircle, Camera, Mail, DollarSign, Clock,
    CheckCircle, Calendar, Globe, Bookmark, Share2, Heart, Instagram
} from 'lucide-react';
import { API_BASE_URL } from '../constants';
import ProfileImage from './ProfileImage';


function formatFollowers(num) {
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
    return num;
}


const ProfileModal = ({
    selectedProfile,
    showProfileModal,
    setShowProfileModal,
    activeProfileTab,
    setActiveProfileTab,
    startReviewFlow,
    handleServiceBookmark,
    bookmarkedServices,
    reviews,
}) => {
    const [reviewLikes, setReviewLikes] = useState({});
    const [imgError, setImgError] = useState(false);
    const handleReviewLike = (reviewId) => {
        setReviewLikes(prev => ({
            ...prev,
            [reviewId]: !prev[reviewId]
        }));
    };

    if (!selectedProfile || !showProfileModal) {
        console.log('ProfileModal not showing - selectedProfile:', !!selectedProfile, 'showProfileModal:', showProfileModal);
        return null;
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="relative">
                    <div className="bg-gradient-to-br from-purple-600 to-pink-600 text-white p-8 rounded-t-2xl">
                        <button
                            onClick={() => setShowProfileModal(false)}
                            className="absolute top-4 right-4 text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                        >
                            <X size={20} />
                        </button>

                        <div className="flex items-center space-x-6">
                            {/* <img
                                src={`${API_BASE_URL}/api/proxy-image?url=${encodeURIComponent(selectedProfile?.profilePicture || selectedProfile.profile_pic_url || selectedProfile.targetBusiness?.instagramData?.profile_pic_url)}`}
                                alt={`${selectedProfile?.displayName || selectedProfile.full_name || selectedProfile?.instagramData?.full_name} profile`}
                                className="w-24 h-24 rounded-full object-cover border-2 border-white shadow-md"
                                onError={() => setImgError(true)}
                            // onError={(e) => {
                            //     e.target.style.display = 'none';
                            //     e.target.nextSibling.style.display = 'flex';
                            // }}
                            /> */}

                            <ProfileImage
                                profilePicture={selectedProfile?.profilePicture || selectedProfile.profile_pic_url || selectedProfile.targetBusiness?.instagramData?.profile_pic_url}
                                profile_pic_base64={selectedProfile?.profile_pic_base64}
                                alt={`${selectedProfile?.displayName || selectedProfile.full_name || selectedProfile?.instagramData?.full_name} profile`}
                                className="w-8 h-8 rounded-full border-2 border-purple-200"
                            />

                            <div>
                                <h2 className="text-3xl font-bold mb-2">{selectedProfile?.displayName || selectedProfile?.instagramData?.full_name}</h2>
                                <p className="text-purple-100 text-lg mb-2">{selectedProfile?.username}</p>
                                <p className="text-purple-100">{selectedProfile?.title}</p>

                                <div className="flex items-center space-x-4 mt-4">
                                    <div className="flex items-center space-x-1">
                                        <Star className="text-yellow-300" size={16} fill="currentColor" />
                                        <span className="font-semibold">{selectedProfile.averageRating}</span>
                                        <span className="text-purple-100">({selectedProfile.reviewCount} reviews)</span>
                                    </div>
                                    <div className="flex items-center space-x-1">
                                        <Users size={16} />
                                        <span>{selectedProfile?.instagramData?.followers || selectedProfile?.followers} followers</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="border-b border-gray-200">
                        <div className="flex space-x-8 px-8">
                            {[
                                { id: 'overview', label: 'Overview', icon: User },
                                { id: 'reviews', label: 'Reviews', icon: MessageCircle },
                                { id: 'portfolio', label: 'Portfolio', icon: Camera },
                                { id: 'contact', label: 'Contact', icon: Mail }
                            ].map(tab => {
                                const Icon = tab.icon;
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveProfileTab(tab.id)}
                                        className={`flex items-center space-x-2 py-4 border-b-2 transition-colors ${activeProfileTab === tab.id
                                            ? 'border-purple-600 text-purple-600 font-medium'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                            }`}
                                    >
                                        <Icon size={16} />
                                        <span>{tab.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <div className="p-8">
                    {activeProfileTab === 'overview' && (
                        <div className="space-y-8">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-3">About</h3>
                                <p className="text-gray-600 leading-relaxed">{selectedProfile?.biography}</p>
                            </div>

                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-3">Specialties</h3>
                                <div className="flex flex-wrap gap-2">
                                    {/* Specialties would be mapped here */}
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Service Information</h3>
                                    <div className="space-y-3">
                                        <div className="flex items-center space-x-2">
                                            <DollarSign size={16} className="text-gray-400" />
                                            <span className="text-gray-600">Starting from</span>
                                            <span className="font-semibold text-purple-600">{selectedProfile?.price}</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <Clock size={16} className="text-gray-400" />
                                            <span className="text-gray-600">Response time:</span>
                                            <span className="font-medium">{selectedProfile?.responseTime}</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <CheckCircle size={16} className="text-gray-400" />
                                            <span className="text-gray-600">Completion rate:</span>
                                            <span className="font-medium">{selectedProfile?.completionRate}%</span>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Availability</h3>
                                    <div className="space-y-3">
                                        <div className="flex items-center space-x-2">
                                            <Calendar size={16} className="text-gray-400" />
                                            <span className="text-gray-600">{selectedProfile?.availability}</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <Globe size={16} className="text-gray-400" />
                                            <span className="text-gray-600">Languages:</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex space-x-4 pt-6 border-t border-gray-200">
                                <button
                                    onClick={() => startReviewFlow(selectedProfile)}
                                    className="flex-1 bg-purple-600 text-white py-3 px-6 rounded-lg hover:bg-purple-700 transition-colors font-medium"
                                >
                                    Write Review
                                </button>
                                <button
                                    onClick={() => handleServiceBookmark(selectedProfile?.username)}
                                    className={`flex items-center space-x-2 px-6 py-3 rounded-lg border transition-colors ${bookmarkedServices.includes(selectedProfile?.username)
                                        ? 'border-purple-600 text-purple-600 bg-purple-50'
                                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    <Bookmark size={16} fill={bookmarkedServices.includes(selectedProfile?.username) ? 'currentColor' : 'none'} />
                                    <span>{bookmarkedServices.includes(selectedProfile?.username) ? 'Saved' : 'Save'}</span>
                                </button>
                                <button className="flex items-center space-x-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                                    <Share2 size={16} />
                                    <span>Share</span>
                                </button>
                            </div>
                        </div>
                    )}

                    {activeProfileTab === 'reviews' && (
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-6">Customer Reviews</h3>
                            <div className="space-y-6">
                                {reviews?.map((testimonial, index) => (
                                    <div key={index} className="border border-gray-200 rounded-lg p-6">
                                        {/* <pre>{JSON.stringify(testimonial, null, 2)}</pre> */}
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center space-x-3">

                                                <div className="flex-shrink-0">
                                                    <ProfileImage
                                                        profilePicture={testimonial.targetBusiness?.profilePicture || testimonial.targetBusiness?.profile_pic_url || testimonial.targetBusiness?.instagramData?.profile_pic_url || selectedProfile?.profilePicture || selectedProfile.profile_pic_url || selectedProfile.targetBusiness?.instagramData?.profile_pic_url}
                                                        profile_pic_base64={testimonial.targetBusiness?.profile_pic_base64}
                                                        alt={`${testimonial.targetBusiness?.displayName || testimonial.targetBusiness?.full_name || testimonial.targetBusiness?.instagramData?.full_name} profile`}
                                                        className="w-10 h-10  rounded-full object-cover border-2 border-white shadow-md"
                                                    />
                                                    {/* {testimonial.targetBusiness?.profilePicture || testimonial.targetBusiness?.profile_pic_url || testimonial.targetBusiness?.instagramData?.profile_pic_url ? (
                                                        <img
                                                            src={`${API_BASE_URL}/api/proxy-image?url=${encodeURIComponent(testimonial.targetBusiness?.profilePicture || testimonial.targetBusiness?.profile_pic_url || testimonial.targetBusiness?.instagramData?.profile_pic_url || selectedProfile?.profilePicture || selectedProfile.profile_pic_url || selectedProfile.targetBusiness?.instagramData?.profile_pic_url)}`}
                                                            alt={`${testimonial.targetBusiness?.displayName || testimonial.targetBusiness?.full_name || testimonial.targetBusiness?.instagramData?.full_name} profile`}
                                                            className="w-10 h-10  rounded-full object-cover border-2 border-white shadow-md"
                                                            onError={() => setImgError(true)}
                                                        // onError={(e) => {
                                                        //     e.target.style.display = 'none';
                                                        //     e.target.nextSibling.style.display = 'flex';
                                                        // }}
                                                        />
                                                    ) :
                                                        <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center text-sm">
                                                            👤
                                                        </div>
                                                    } */}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 truncate">
                                                        {testimonial.targetBusiness?.username || testimonial.targetBusinessUsername || testimonial.targetBusiness?.displayName || testimonial.businessName || 'Unknown'}
                                                    </p>

                                                    <div className="flex items-center space-x-2">
                                                        {testimonial.rating?.overall && (
                                                            <div className="flex items-center space-x-1">
                                                                <Star className="text-yellow-400" size={12} fill="currentColor" />
                                                                <span className="text-xs font-medium">{(testimonial.rating?.overall ?? 0).toFixed(1)}</span>
                                                            </div>
                                                        )}
                                                        {testimonial.averageRating && (
                                                            <div className="flex items-center space-x-1">
                                                                <Star className="text-yellow-400" size={12} fill="currentColor" />
                                                                <span className="text-xs font-medium">{(testimonial.averageRating ?? 0).toFixed(1)}</span>
                                                            </div>
                                                        )}
                                                        {testimonial.reviewCount && (
                                                            <span className="text-xs text-gray-500">(📝 {testimonial.reviewCount || 0})</span>
                                                        )}
                                                        {(testimonial.targetBusiness?.instagramData?.followers != null || testimonial.targetBusiness?.followers != null) && (
                                                            <span className="text-xs text-gray-500">
                                                                <Users size={10} className="inline-block align-middle" /> {formatFollowers(testimonial.targetBusiness?.instagramData?.followers ?? testimonial.targetBusiness?.followers ?? 0)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                            </div>
                                            <button
                                                onClick={() => handleReviewLike(index)}
                                                className={`flex items-center space-x-1 transition-colors ${reviewLikes[index] ? 'text-red-500' : 'text-gray-400 hover:text-red-500'
                                                    }`}
                                            >
                                                <Heart size={16} fill={reviewLikes[index] ? 'currentColor' : 'none'} />
                                                <span className="text-sm">12</span>
                                            </button>
                                        </div>
                                        <p className="text-gray-700">{testimonial.comment || testimonial.content?.comment}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {activeProfileTab === 'portfolio' && (
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-6">Portfolio</h3>
                            <div className="grid grid-cols-3 gap-4">
                                {selectedProfile.targetBusiness?.portfolioImages?.map((image, index) => (
                                    <div key={index} className="aspect-square bg-gradient-to-br from-purple-400 to-pink-400 rounded-lg flex items-center justify-center text-4xl">
                                        {image}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {activeProfileTab === 'contact' && (
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-6">Contact Information</h3>
                            <div className="space-y-4">
                                <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                                    <Instagram className="text-purple-600" size={20} />
                                    <div>
                                        <p className="font-medium">Instagram</p>
                                        <p className="text-gray-600">{selectedProfile.username}</p>
                                    </div>
                                </div>

                                <button className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg hover:bg-purple-700 transition-colors font-medium">
                                    Contact Provider
                                </button>

                                <p className="text-sm text-gray-500 text-center">
                                    Response time: {selectedProfile.responseTime}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div >
    );
};

export default ProfileModal;
